import React from 'react';
import { FileDropZone } from '../common/FileDropZone';
import { formatBytes } from '../../utils/assetValidation';
import type { AssetKey, UploadedAsset } from '../../types';

interface AssetSlotProps {
  label: string;
  assetKey: AssetKey;
  asset?: UploadedAsset;
  isUploading?: boolean;
  error?: string;
  required?: boolean;
  hint?: string;
  onUpload: (file: File) => void;
  onRemove: () => void;
}

export function AssetSlot({
  label,
  assetKey,
  asset,
  isUploading = false,
  error,
  required = false,
  hint,
  onUpload,
  onRemove,
}: AssetSlotProps) {
  return (
    <div className="space-y-1.5">
      {/* Label row */}
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-zinc-300">
          {label}
          {required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
        {asset && !isUploading && (
          <button
            type="button"
            onClick={onRemove}
            className="text-[10px] text-zinc-500 hover:text-red-400 transition-colors"
            aria-label={`Remove ${label}`}
          >
            Remove
          </button>
        )}
      </div>

      {/* Slot body */}
      {isUploading ? (
        <UploadingState />
      ) : asset ? (
        <AssetPreview asset={asset} />
      ) : (
        <FileDropZone assetKey={assetKey} onFile={onUpload} compact />
      )}

      {/* Error */}
      {error && !isUploading && (
        <p className="text-[10px] text-red-400 leading-snug">{error}</p>
      )}

      {/* Hint */}
      {hint && !error && !asset && !isUploading && (
        <p className="text-[10px] text-zinc-500 leading-snug">{hint}</p>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────

function UploadingState() {
  return (
    <div className="rounded-lg border border-zinc-700 bg-zinc-800/50 p-2.5 flex items-center gap-2">
      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin shrink-0" />
      <span className="text-xs text-zinc-400">Uploading…</span>
    </div>
  );
}

function AssetPreview({ asset }: { asset: UploadedAsset }) {
  return (
    <div className="rounded-lg border border-zinc-600 bg-zinc-800 p-2 flex items-center gap-2.5 overflow-hidden">
      {/* Thumbnail / icon */}
      <div className="w-10 h-10 rounded shrink-0 overflow-hidden bg-zinc-700 flex items-center justify-center">
        {asset.thumbnailUrl ? (
          <img
            src={asset.thumbnailUrl}
            alt=""
            className="w-full h-full object-cover"
            aria-hidden="true"
          />
        ) : (
          <AssetTypeIcon type={asset.type} />
        )}
      </div>

      {/* File info */}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-zinc-200 truncate">{asset.name}</p>
        <p className="text-[10px] text-zinc-500 mt-0.5">
          {formatBytes(asset.size)}
          {asset.duration != null && ` · ${asset.duration}s`}
          {asset.width != null && asset.height != null && ` · ${asset.width}×${asset.height}`}
        </p>
      </div>

      {/* Success tick */}
      <div className="w-4 h-4 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
        <svg className="w-2.5 h-2.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
    </div>
  );
}

function AssetTypeIcon({ type }: { type: 'image' | 'video' | 'audio' }) {
  const cls = 'w-5 h-5 text-zinc-400';
  if (type === 'image') return (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 19.5h18M3.75 4.5h16.5A.75.75 0 0121 5.25v13.5a.75.75 0 01-.75.75H3.75A.75.75 0 013 18.75V5.25A.75.75 0 013.75 4.5z" />
    </svg>
  );
  if (type === 'video') return (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9A2.25 2.25 0 0013.5 5.25h-9a2.25 2.25 0 00-2.25 2.25v9A2.25 2.25 0 004.5 18.75z" />
    </svg>
  );
  return (
    <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 11-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z" />
    </svg>
  );
}
